/**
 * Affiliate referral capture (dashboard side).
 *
 * When an affiliate links to dashboard.<domain>?ref=CODE, store CODE in the
 * `wfc_ref` cookie scoped to the parent domain (.webforcheap.com) so it is
 * readable by api.<domain> when the user signs up and creates a site. Mirrors
 * the capture snippet the sites worker injects into the apex marketing site.
 *
 * Last-touch: a fresh valid ?ref= overwrites any prior value. 90-day window.
 */

const MAX_AGE = 60 * 60 * 24 * 90; // 90 days in seconds

/** Parent registrable-ish domain, e.g. dashboard.webforcheap.com -> webforcheap.com */
function parentDomain(): string {
  return location.hostname.split('.').slice(-2).join('.');
}

/** Validate a raw referral code the same way the API does. */
function sanitize(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const c = raw.trim();
  if (!c || c.length > 64 || !/^[A-Za-z0-9_-]+$/.test(c)) return null;
  return c;
}

/**
 * Read ?ref= from the current URL (query string or hash query) and persist it
 * to the parent-domain cookie. Safe to call on every boot; no-ops without a ref.
 */
export function captureRefFromUrl(): void {
  try {
    const hashQ = location.hash.includes('?') ? location.hash.slice(location.hash.indexOf('?')) : '';
    const raw =
      new URLSearchParams(location.search).get('ref') ||
      new URLSearchParams(hashQ).get('ref');
    const code = sanitize(raw);
    if (!code) return;

    const secure = location.protocol === 'https:' ? ';Secure' : '';
    document.cookie = `wfc_ref=${code};Domain=.${parentDomain()};Path=/;Max-Age=${MAX_AGE};SameSite=Lax${secure}`;
  } catch {
    /* never let referral capture break app boot */
  }
}
